import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  persistUiLanguage,
  resolveInitialUiLanguage,
  type UiLanguage,
} from "services/i18n";
import { applyMomentLocale } from "services/i18n/dateLocale";
import { logError } from "services/logging";
import { patchUser } from "services/users";
import { updateUserState as updateLocalUser, useUser } from "./UserContext";

interface LocalizationContextType {
  language: UiLanguage;
  changeLanguage: (lang: UiLanguage) => void;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);
LocalizationContext.displayName = "LocalizationContext";

// Owns the canonical UI language for the app. It resolves the initial value
// from user preference, local storage, and browser settings, then keeps i18n,
// moment's date locale, local storage, and document.documentElement.lang
// synchronized with that single provider state.
export function LocalizationProvider({ children }: PropsWithChildren) {
  const user = useUser(false);
  const { i18n } = useTranslation();

  const [language, setLanguageState] = useState<UiLanguage>(() =>
    resolveInitialUiLanguage(user?.localization_lang)
  );

  const syncLanguage = useCallback(
    async (lang: UiLanguage) => {
      persistUiLanguage(lang);
      document.documentElement.lang = lang;

      // Awaited before the copy switches: moment's locale is global state React does not track,
      // so changeLanguage's re-render is the only thing that repaints dates in the new language.
      await applyMomentLocale(lang);

      if (i18n.resolvedLanguage !== lang) {
        void i18n.changeLanguage(lang);
      }
    },
    [i18n]
  );

  const changeLanguage = useCallback(
    (lang: UiLanguage) => {
      setLanguageState(lang);

      if (user && user.localization_lang !== lang) {
        updateLocalUser({ ...user, localization_lang: lang });
        patchUser({ localization_lang: lang }).catch((error: unknown) => {
          logError("Failed to update user language", error);
        });
      }
    },
    [user]
  );

  useEffect(() => {
    // Keep the browser environment in sync with the canonical provider state.
    void syncLanguage(language);
  }, [language, syncLanguage]);

  useEffect(() => {
    // Reconcile provider state when the authenticated user's saved preference changes.
    setLanguageState(resolveInitialUiLanguage(user?.localization_lang));
  }, [user?.localization_lang]);

  const contextValue = useMemo(
    () => ({ language, changeLanguage }),
    [language, changeLanguage]
  );

  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  );
}

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error("useLocalization must be used within a LocalizationProvider");
  }
  return context;
};
